import React, { useState, useContext, useMemo, useEffect } from "react";
import { X, Users, Mail } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import { SessionContext } from "../../../../lib/context/SessionContext";
import SessionAPI from "../../../../lib/api/Session";
import Button from "../../../common/Button";
import SortableTable from "./SortableTable";
import UserDetail from "../views/UserDetail";
import { groupUsers } from "../userUtils";
import "./LicenseErrorModal.css";

export default function LicenseErrorModal({
  isOpen,
  onClose,
  roleUuid,
  errorMessage,
  details = [],
  context = "role", // "role" or "user"
  companyUuids = null, // restrict "user" context to these companies' users
}) {
  const { users, roles, companies, fetchUsers, fetchRoles, fetchLicenses } =
    useContext(FieldConfigurationContext);

  // Make SessionContext optional since it might not be available in all contexts
  const sessionContext = useContext(SessionContext);

  const [showUsersList, setShowUsersList] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [companyName, setCompanyName] = useState("Your Company");

  // Fetch company name from multiple sources
  useEffect(() => {
    const getCompanyName = async () => {
      // Try to get company name from SessionContext first
      // Check multiple possible locations in the session data
      let name = null;

      // Check sessionContext.data.company.name
      if (sessionContext?.data?.company?.name) {
        name = sessionContext.data.company.name;
      }
      // Check sessionContext.data.name (from /authorization response)
      else if (sessionContext?.data?.name) {
        name = sessionContext.data.name;
      }
      // Check sessionContext.company.name
      else if (sessionContext?.company?.name) {
        name = sessionContext.company.name;
      }

      // Check sessionStorage for company name resolved during permission check (user scope)
      if (!name) {
        name = sessionStorage.getItem("field_company_name") || null;
      }

      // If we found a name, use it
      if (name) {
        setCompanyName(name);
        return;
      }

      // If no company name in session, try to fetch from API
      try {
        const token = sessionStorage.getItem("token");
        if (token) {
          const response = await SessionAPI.validateToken(token);
          if (response.status === 200 && response.data?.name) {
            setCompanyName(response.data.name);
          }
        }
      } catch (error) {
        console.error("Failed to fetch company name:", error);
        // Keep default "Your Company"
      }
    };

    getCompanyName();
  }, [sessionContext]);

  // Get the role data
  const role = useMemo(() => {
    if (!roles?.data || !roleUuid) return null;
    return roles.data.find((r) => r.uuid === roleUuid);
  }, [roles?.data, roleUuid]);

  // Get users consuming licenses for the violated capabilities
  const affectedUsers = useMemo(() => {
    if (!users?.data || !roles?.data) return [];

    // Build roles map for name resolution
    const rolesMap = roles.data.reduce((map, r) => {
      map[r.uuid] = r;
      return map;
    }, {});

    let filteredUsers;

    if (context === "user" && details.length > 0) {
      // Map detail names to role capability flags
      const capabilityFlags = details
        .map((d) => {
          if (d.name === "Time Entry") return "enableTime";
          if (d.name === "Expense") return "enableExpense";
          if (d.name === "Project Management") return "enableProjectManagement";
          if (d.name === "Service Management") return "enableServiceManagement";
          return null;
        })
        .filter(Boolean);

      // Active users whose role has any of the violated capabilities enabled,
      // restricted to the company/companies this operation actually affects —
      // users.data can span every company an admin manages, but the license
      // limit is only actionable against the company(ies) being saved here.
      filteredUsers = users.data.filter((user) => {
        if (user.active === false) return false;
        if (
          companyUuids &&
          companyUuids.length > 0 &&
          !companyUuids.includes(user.company_uuid)
        ) {
          return false;
        }
        const userRole = rolesMap[user.roleUuid];
        if (!userRole) return false;
        return capabilityFlags.some((flag) => userRole[flag]);
      });
    } else {
      // Role context: active users assigned to this specific role
      if (!roleUuid || !role) return [];
      filteredUsers = users.data.filter(
        (user) => user.roleUuid === roleUuid && user.active !== false
      );
    }

    // Enrich with resolved role names
    const enriched = filteredUsers.map((user) => {
      const userRole = rolesMap[user.roleUuid];
      return {
        ...user,
        role: userRole?.name || "Unknown Role",
      };
    });

    // Group by email so multi-company users appear as one row and carry
    // companyUuids/uuids arrays that UserDetail/UserForm expect
    const companiesMap = companies?.data || {};
    return groupUsers(enriched, companiesMap);
  }, [
    users?.data,
    roles?.data,
    companies?.data,
    roleUuid,
    role,
    context,
    details,
    companyUuids,
  ]);

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return affectedUsers;

    const sorted = [...affectedUsers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for name (combine firstName and lastName)
      if (sortConfig.key === "firstName") {
        aValue = `${a.firstName || ""} ${a.lastName || ""}`;
        bValue = `${b.firstName || ""} ${b.lastName || ""}`;
      }

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (typeof aValue === "boolean") {
        return sortConfig.direction === "asc"
          ? aValue === bValue
            ? 0
            : aValue
            ? -1
            : 1
          : aValue === bValue
          ? 0
          : aValue
          ? 1
          : -1;
      }

      return 0;
    });

    return sorted;
  }, [affectedUsers, sortConfig]);

  // Handle sort
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle user selection
  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  // Handle back from user detail
  const handleBackFromUserDetail = () => {
    setSelectedUser(null);
  };

  // Handle user save - refresh data and check if error still exists
  const handleUserSave = async (updatedUser) => {
    setSelectedUser(null);

    // Refresh all data
    await Promise.all([
      fetchUsers(true),
      fetchRoles(true),
      fetchLicenses(true),
    ]);

    // After updating, go back to the error screen
    // The user can then try the operation again or close the modal
  };

  // Handle Contact Sales button
  const handleContactSales = () => {
    // Get capability names from details
    const capabilities = details.map((detail) => detail.name || "").join(", ");

    // Construct email
    const toEmail = "Computereasesales@deltek.com";
    const subject = `ComputerEase Field ${capabilities} License Update for ${companyName}`;

    // Open email client with empty body
    window.location.href = `mailto:${toEmail}?subject=${encodeURIComponent(
      subject
    )}`;
  };

  // Handle modal close - allow closing via X button
  const handleClose = () => {
    setShowUsersList(false);
    setSelectedUser(null);
    onClose();
  };

  // Reset view when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowUsersList(false);
      setSelectedUser(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Table columns for users list
  const columns = [
    {
      key: "firstName",
      label: "Name",
      sortable: true,
      render: (user) => (
        <span style={{ fontWeight: 500, color: "rgba(46, 50, 66, 1)" }}>
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
    },
    {
      key: "companyNames",
      label: "Company",
      sortable: true,
      render: (user) => (user.companyNames || []).join(", ") || "—",
    },
    {
      key: "active",
      label: "Status",
      sortable: true,
      width: "100px",
      align: "center",
      render: (user, value) => (
        <span
          className={`user-status-badge ${
            value ? "user-status-badge--active" : "user-status-badge--inactive"
          }`}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  return (
    <div className="license-error-modal-overlay">
      <div className="license-error-modal">
        {/* Close button — hidden when viewing a user detail */}
        {!selectedUser && (
          <button
            className="license-error-modal__close"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        )}

        {/* Show user detail if selected */}
        {selectedUser ? (
          <UserDetail
            user={selectedUser}
            onBack={handleBackFromUserDetail}
            onUpdate={handleUserSave}
          />
        ) : showUsersList ? (
          /* Show users list */
          <div className="license-error-modal__users-list">
            <div className="license-error-modal__header">
              <h2 className="license-error-modal__title">
                {context === "user"
                  ? "Active Users Consuming Licenses"
                  : `Active Users in ${role?.name || "Role"}`}
              </h2>
              <p className="license-error-modal__subtitle">
                {sortedUsers.length}{" "}
                {sortedUsers.length === 1 ? "user" : "users"}{" "}
                {context === "user"
                  ? "currently consuming affected license seats"
                  : "assigned to this role"}
              </p>
            </div>

            <div className="license-error-modal__content">
              {sortedUsers.length === 0 ? (
                <div className="license-error-modal__empty">
                  <p>No users assigned to this role.</p>
                </div>
              ) : (
                <SortableTable
                  data={sortedUsers}
                  columns={columns}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  onRowClick={handleUserClick}
                  emptyMessage="No users found."
                />
              )}
            </div>

            <div className="license-error-modal__footer">
              <Button
                className="button--secondary"
                onClick={() => setShowUsersList(false)}
              >
                Back to Error
              </Button>
            </div>
          </div>
        ) : (
          /* Show error message */
          <>
            <div className="license-error-modal__header">
              <h2 className="license-error-modal__title">
                {context === "user"
                  ? "Unable to Add User to Role"
                  : "Unable to Enable Capability"}
              </h2>
              <p className="license-error-modal__message">
                {context === "user"
                  ? errorMessage ||
                    "This user can't be added to the selected role because one or more enabled capabilities in the role have reached their license limit."
                  : "This capability can't be enabled for the role because adding it would exceed the number of available licenses."}
              </p>
            </div>

            <div className="license-error-modal__content">
              <h3 className="license-error-modal__section-title">
                License Usage
              </h3>
              {details.length > 0 ? (
                <ul className="license-error-modal__details">
                  {details.map((detail, index) => (
                    <li
                      key={index}
                      className="license-error-modal__detail-item"
                    >
                      <span className="license-error-modal__detail-name">
                        {detail.name}:
                      </span>
                      <span className="license-error-modal__detail-usage">
                        {detail.used} of {detail.total} licenses in use
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="license-error-modal__no-details">
                  No detailed license information available.
                </p>
              )}

              <p className="license-error-modal__help-text">
                To continue, remove a user from the role or contact sales to
                purchase additional licenses.
              </p>
            </div>

            <div className="license-error-modal__footer">
              <Button
                className="button--secondary"
                onClick={() => setShowUsersList(true)}
              >
                <Users size={18} style={{ marginRight: "6px" }} />
                Active Users
              </Button>
              <Button className="button--primary" onClick={handleContactSales}>
                <Mail size={18} style={{ marginRight: "6px" }} />
                Contact Sales
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
